import { ListBaseModel } from '@/api/models';
import { t } from '@/i18n';
import {
  BackendBaseModel,
  DateRangeSchema,
  DayjsSchema,
  alphanumericStringType,
  booleanStringType,
  paginationSearchParamSchema,
} from '@/utils';
import { z } from 'zod';
import { RoleModel } from './role';
import { UserBaseModel } from './user';

/// ////////////////////////////////////////////
/// Enum
/// ////////////////////////////////////////////

export const exclusionSettingsOperation = z.enum(['exclude', 'include']);

export const ExclusionSettingsOperationLabelModifier = {
  exclude: t('exclude-from-options'),
  include: t('include-in-options'),
} as const satisfies Record<z.infer<typeof exclusionSettingsOperation>, string>;

/// ////////////////////////////////////////////
/// Model
/// ////////////////////////////////////////////

/**
 * OrganizationBaseModel
 * ExternalSystemCode is optional because used only in specific tenants of BnS.
 */
export const OrganizationBaseModel = BackendBaseModel.extend({
  startYear: z.number().int().nullable(),
  endYear: z.number().int().nullable(),
  code: alphanumericStringType.min(1).max(32),
  externalSystemCode: alphanumericStringType.min(1).max(32).nullish(),
  name: z.string().min(1),
  level: z.number().int(),
  upperId: z.string().uuid().nullable(),
  roleId: z.string().uuid().nullable(),
  isIntegrated: z.boolean(),
  isExcludedFromOptions: z.boolean(),
});

const UsersModel = UserBaseModel.array();

type OrganizationsElementType = z.infer<typeof OrganizationBaseModel> & {
  upper?: z.infer<typeof OrganizationBaseModel> | null;
  role?: z.infer<typeof RoleModel> | null;
  users?: z.infer<typeof UsersModel> | null;
};

const OrganizationsModelElement: z.ZodType<OrganizationsElementType> =
  OrganizationBaseModel.extend({
    upper: OrganizationBaseModel.nullish(),
    role: RoleModel.nullish(),
    users: UsersModel.nullish(),
  });

export const OrganizationsModel = OrganizationsModelElement.array();

type OrganizationType = z.infer<typeof OrganizationBaseModel> & {
  users: Array<z.infer<typeof UserBaseModel>>;
  role?: z.infer<typeof RoleModel> | null;
  upper?: z.infer<typeof OrganizationBaseModel> | null;
};

type OrganizationUpperModelType =
  | (z.infer<typeof OrganizationBaseModel> & {
      upper?: OrganizationUpperModelType;
    })
  | undefined
  | null;

const OrganizationUpperModel: z.ZodType<OrganizationUpperModelType> =
  OrganizationBaseModel.extend({
    upper: z.lazy(() => OrganizationUpperModel),
  }).nullish();

export const OrganizationModel: z.ZodType<OrganizationType> =
  OrganizationBaseModel.extend({
    users: UserBaseModel.array(),
    role: z.optional(RoleModel.nullable()),
    upper: z.lazy(() => OrganizationUpperModel).nullish(),
  });

export const OrganizationRecursiveReturnModel: z.ZodType<
  Array<
    Omit<OrganizationType, 'users'> & {
      users?: Array<z.infer<typeof UserBaseModel>> | null;
    }
  >
> = OrganizationBaseModel.extend({
  users: UserBaseModel.array().nullish(),
  role: RoleModel.nullish(),
  upper: z.lazy(() => OrganizationUpperModel).nullish(),
}).array();

const OrganizationNestedResultBaseModel = OrganizationBaseModel.pick({
  id: true,
  createdAt: true,
  updatedAt: true,
  code: true,
  externalSystemCode: true,
  name: true,
  level: true,
  startYear: true,
  endYear: true,
  isIntegrated: true,
  isExcludedFromOptions: true,
}).extend({
  roleName: z.string().nullable(),
  userCount: z.number().int().nullable(),
});

type OrganizationNestedResultModelType = z.infer<
  typeof OrganizationNestedResultBaseModel
> & {
  descendant: OrganizationNestedResultModelType[];
};

const OrganizationNestedResultModel: z.ZodType<OrganizationNestedResultModelType> =
  OrganizationNestedResultBaseModel.extend({
    descendant: z.lazy(() => OrganizationNestedResultModel.array()),
  });

export const OrganizationNestedResultsModel =
  OrganizationNestedResultModel.array();

export const OrganizationNestedListModel = ListBaseModel.extend({
  results: OrganizationNestedResultsModel,
});

const BaseOrganizationWithUpperModel = OrganizationBaseModel.omit({
  upperId: true,
});

type OrganizationWithUpperType = z.infer<
  typeof BaseOrganizationWithUpperModel
> & {
  upperOrganization?: z.infer<typeof BaseOrganizationWithUpperModel> | null;
};

const OrganizationWithUpperModel: z.ZodType<OrganizationWithUpperType> =
  BaseOrganizationWithUpperModel.extend({
    upperOrganization: z.lazy(() => OrganizationWithUpperModel).nullish(),
  });

export const OrganizationWithUppersModel = OrganizationWithUpperModel.array();

/// ////////////////////////////////////////////
/// Schema
/// ////////////////////////////////////////////

export const OrganizationSearchFormSchema = OrganizationBaseModel.pick({
  code: true,
  name: true,
})
  .extend({
    roleId: RoleModel.shape.id,
    referenceFiscalYear: DayjsSchema().optional().catch(undefined),
    showExcludedOptions: z.boolean(),
  })
  .partial();

export const UpdateOrganizationMemberSchema = z.object({
  id: z.string().uuid(),
  userIds: z.string().uuid().array(),
});

export const OrganizationFormSchema = OrganizationBaseModel.pick({
  code: true,
  name: true,
  externalSystemCode: true,
}).extend({
  upperOrganizationIds: OrganizationBaseModel.shape.upperId.array().nullish(),
  roleId: OrganizationBaseModel.shape.roleId.optional(),
  period: DateRangeSchema.nullish(),
});

export const BulkUpdateExcludedFlagsFormSchema = z.object({
  operation: exclusionSettingsOperation,
  organizationIds: z.string().uuid().array(),
});

export const BulkUpdateExcludedFlagsResponseModel = z.object({
  status: z.string(),
  requestedCount: z.number().int(),
});

export const OrganizationSearchUrlSchema = z.object({
  referenceFiscalYear: z.string().optional(),
  showExcludedOptions: booleanStringType.optional(),
  page: paginationSearchParamSchema.shape.page,
});
